<script>
import { computed, defineComponent, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import Carousel from '@/components/pdp/legacy/image-gallery/Carousel.vue';
import CarouselItem from '@/components/pdp/legacy/image-gallery/CarouselItem.vue';
import ZoomOnHover from '@/components/pdp/legacy/ZoomOnHover.vue';

export default defineComponent({
  name: 'ImageGallery',
  props: {
    images: { required: false, type: Array },
    productName: { required: true, type: String },
    titleImage: { required: false, type: Object },
  },
  components: {
    ZoomOnHover,
    Carousel,
    CarouselItem,
  },
  setup(props) {
    const route = useRoute();

    const previewImage = ref({
      large: '',
      medium: '',
      small: '',
      thumb: '',
      url: '',
      zoom: '',
    });

    const chosenImage = computed(() => previewImage.value.large || props.titleImage?.large || null);
    const chosenZoomImage = computed(
      () => previewImage.value.zoom || props.titleImage?.zoom || null,
    );
    const spaceBetween = computed(() => props.images?.length > 4);

    const chooseThumb = (index) => {
      previewImage.value = props.images[index];
    };

    watch(
      () => route.path,
      () => {
        previewImage.value = {
          large: '',
          medium: '',
          small: '',
          thumb: '',
          url: '',
          zoom: '',
        };
      },
    );

    return {
      chosenImage,
      chosenZoomImage,
      chooseThumb,
      previewImage,
      spaceBetween,
    };
  },
});
</script>

<template>
  <section v-if="images && images.length > 0">
    <div class="gallery" data-test="gallery">
      <div class="hero-image" data-test="hero-image">
        <ZoomOnHover
          :img-normal="chosenImage"
          :img-zoom="chosenZoomImage"
          :productName="productName"
          tabindex="0"
        />
      </div>
      <div
        v-if="images.length > 1"
        class="thumbs"
        :class="{ 'space-between': spaceBetween }"
        data-test="first-image"
      >
        <a
          v-for="(image, index) in images"
          :key="index"
          :class="{ active: chosenImage === image.large }"
          @mouseover="chooseThumb(index)"
          @keydown.space.prevent="chooseThumb(index)"
        >
          <img :src="image.small" :alt="`${productName} photo ${index + 1}`" tabindex="0" />
        </a>
      </div>
    </div>
    <Carousel data-test="gallery-carousel" :watchItems="images">
      <CarouselItem
        v-for="(item, index) in images"
        :key="index"
        class="hero-image"
        data-test="hero-image-carousel"
      >
        <img
          :src="item.large"
          class="object-contain fixed-image-size"
          :alt="`${productName} photo ${index + 1}`"
          :fetchpriority="index === 0 ? 'high' : 'low'"
          tabindex="0"
        />
      </CarouselItem>
    </Carousel>
  </section>
</template>

<style lang="scss" scoped>
.fixed-image-size {
  @apply h-60 max-h-60;
  object-fit: contain;
}
@include respond-min($screen-md-min) {
  .gallery {
    display: block;
    margin-bottom: 20px;
  }
}

.hero-image {
  vertical-align: middle;
  overflow: hidden;
  position: relative;
  border: 2px solid $alto;
  @include roundedBySize(10px);
}
</style>
